import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useIonAlert } from '@ionic/react';
import { AlertTriangle, Banknote, Clock, ExternalLink, HandCoins, HelpCircle, MessageCircle, Route as RouteIcon, Star } from 'lucide-react';
import { Avatar, Button, Card, ErrorState, Money, Notice, RequestStatusBadge, Spinner } from '@tcrd/ui';
import { Page } from '../../../components/Page';
import { MapView } from '../../../components/map/MapView';
import { ServiceIcon } from '../../../components/ServiceIcon';
import { useToast } from '../../../components/toast';
import { api, errorMessage } from '../../../lib/api';
import { fmtDateTime, fmtDuration, fmtKm, relativeMinutes } from '../../../lib/format';
import type { RideRequestDto } from '../../../lib/types';
import { routeMarkers } from '../../customer/RequestDetailPage';
import { QuoteForm, type QuotePayload } from './QuoteForm';
import { TripNeeds } from '../../../components/TripExtras';

/** "Hace 12 seg", "Hace 3 min"… */
function since(iso: string) {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `Hace ${s} seg`;
  if (s < 3600) return `Hace ${Math.floor(s / 60)} min`;
  return `Hace ${Math.floor(s / 3600)} h`;
}

/** Nueva solicitud de viaje (vista del conductor): cliente, ruta, precio del cliente y Aceptar / Rechazar. */
export function DriverRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [alert] = useIonAlert();
  const [busy, setBusy] = useState(false);
  const [counter, setCounter] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 10_000);
    return () => clearInterval(t);
  }, []);
  const q = useQuery({ queryKey: ['ride-request', id], queryFn: () => api<RideRequestDto>(`/ride-requests/${id}`) });
  const r = q.data;
  const markers = useMemo(() => (r ? routeMarkers(r) : []), [r]);
  const pending = r?.quotes.find((x) => x.status === 'PENDING');
  const open = r && ['REQUESTED', 'INFO_REQUESTED', 'QUOTED'].includes(r.status);

  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['ride-request', id] }), qc.invalidateQueries({ queryKey: ['ride-requests'] })]);

  const sendQuote = async (payload: QuotePayload) => {
    setBusy(true);
    try {
      await api(`/ride-requests/${id}/quotes`, { body: payload });
      await refresh();
      setCounter(false);
      toast('Cotización enviada al cliente', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };

  const action = (path: string, body: Record<string, unknown>, ok: string) =>
    api(`/ride-requests/${id}/${path}`, { body })
      .then(refresh)
      .then(() => toast(ok))
      .catch((e) => toast(errorMessage(e), 'danger'));

  const withdraw = () => pending && api(`/quotes/${pending.id}/withdraw`, { method: 'POST' }).then(refresh).catch((e) => toast(errorMessage(e), 'danger'));
  const reject = () =>
    alert({
      header: 'Rechazar solicitud',
      inputs: [{ name: 'reason', type: 'text', placeholder: 'Motivo para el cliente (opcional)' }],
      buttons: [{ text: 'Volver', role: 'cancel' }, { text: 'Rechazar', role: 'destructive', handler: (v: { reason: string }) => void action('reject', { reason: v.reason || undefined }, 'Solicitud rechazada') }],
    });

  // Precio a aceptar: el que ofrece el cliente o, si no ofreció, la tarifa sugerida.
  const price = r?.offeredFare ?? r?.suggestedFare?.total ?? null;
  const rating = Number(r?.customer?.rating ?? 0);

  return (
    <Page title="Nueva solicitud de viaje" back="/driver/requests" onRefresh={() => q.refetch()}>
      {q.isLoading && <Spinner />}
      {q.error && <ErrorState message={errorMessage(q.error)} onRetry={() => q.refetch()} />}
      {r && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-2">
            {r.customer?.linked ? <span className="rounded-full bg-green-600 text-white px-3 py-1 text-xs font-semibold">✓ Cliente vinculado</span> : <RequestStatusBadge status={r.status} />}
            <span className="text-xs text-[var(--text-muted)]">{since(r.createdAt)}</span>
          </div>

          {r.customer && (
            <Card className="p-4 flex items-center gap-4">
              <Avatar src={r.customer.photoUrl} firstName={r.customer.firstName} lastName={r.customer.lastName} size={64} />
              <div className="flex-1 min-w-0">
                <p className="text-lg font-bold truncate" data-person>{r.customer.firstName} {r.customer.lastName}</p>
                <p className="text-sm flex items-center gap-1">
                  <Star className="size-4 fill-current text-amber-500" aria-hidden />
                  <strong>{rating > 0 ? rating.toFixed(1) : 'Nuevo'}</strong>
                  <span className="text-[var(--text-muted)]">({r.customer.tripsWithYou} viaje{r.customer.tripsWithYou === 1 ? '' : 's'} contigo)</span>
                </p>
                {r.customer.frequent && <span className="mt-1 inline-block rounded-full bg-blue-100 text-blue-800 dark:bg-blue-500/20 dark:text-blue-200 px-2.5 py-0.5 text-xs font-semibold">Cliente frecuente</span>}
              </div>
            </Card>
          )}

          <Card className="p-4 flex gap-3">
            <ol className="flex-1 min-w-0 flex flex-col gap-3 text-sm">
              <li className="flex gap-3">
                <span className="mt-1 size-3 shrink-0 rounded-full border-[3px] border-blue-600" aria-hidden />
                <span><span className="block text-xs text-[var(--text-muted)]">Punto de recogida</span><span className="font-medium">{r.origin.address}</span></span>
              </li>
              {r.stops.map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="mt-1 size-3 shrink-0 rounded-full bg-gray-400" aria-hidden />
                  <span><span className="block text-xs text-[var(--text-muted)]">Parada {i + 1}</span>{s.address}</span>
                </li>
              ))}
              <li className="flex gap-3">
                <span className="mt-1 size-3 shrink-0 rounded-full bg-brand-red" aria-hidden />
                <span><span className="block text-xs text-[var(--text-muted)]">Destino</span><span className="font-medium">{r.destination.address}</span></span>
              </li>
            </ol>
          </Card>

          <Card className="p-3 grid grid-cols-3 gap-2 text-sm">
            <span className="flex items-center gap-1.5"><RouteIcon className="size-4 text-[var(--text-muted)]" aria-hidden /> {r.route ? fmtKm(r.route.distanceMeters) : '—'}</span>
            <span className="flex items-center gap-1.5"><Clock className="size-4 text-[var(--text-muted)]" aria-hidden />
              <span>{r.route ? fmtDuration(r.route.durationSeconds) : '—'}{r.route?.provider === 'TOMTOM' && <span className="block text-[10px] text-[var(--text-muted)]">con tráfico</span>}</span>
            </span>
            <span className="flex items-center gap-1.5 min-w-0"><ServiceIcon name={null} className="size-4 text-[var(--text-muted)] shrink-0" /> <span className="truncate">{r.service.name}</span></span>
          </Card>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="flex items-center gap-1.5 rounded-2xl border border-[var(--border)] px-3 py-2"><Banknote className="size-4 text-[var(--text-muted)]" aria-hidden /> Pago: efectivo o transferencia</span>
            <span className="flex items-center gap-1.5 rounded-2xl border border-[var(--border)] px-3 py-2"><HandCoins className="size-4 text-[var(--text-muted)]" aria-hidden /> Propina: opcional</span>
          </div>
          <p className="text-sm text-[var(--text-muted)]">{r.mode === 'NOW' ? 'Para ahora' : r.mode === 'RECURRING' ? 'Viaje recurrente' : fmtDateTime(r.scheduledAt)}{r.roundTrip ? ` · regreso ${fmtDateTime(r.returnAt)}` : ''} · {r.passengers} pasajero(s) · {r.luggage} maleta(s)</p>

          <TripNeeds needs={r.needs} flightNumber={r.flightNumber} pkg={r.package} />
          {r.notes && <p className="rounded-xl bg-[var(--surface-2)] p-3 text-sm">“{r.notes}”</p>}

          {r.documentsOk === false && <Notice tone="danger">Tienes documentos vencidos: no podrás cotizar hasta actualizarlos y que sean verificados.</Notice>}
          {r.conflicts && r.conflicts.length > 0 && (
            <Notice tone="warning">
              <span className="flex items-center gap-2 font-semibold"><AlertTriangle className="size-4" aria-hidden /> Conflicto de agenda</span>
              {r.conflicts.map((c) => `${c.label} (${fmtDateTime(c.start)})`).join(', ')}. Propón otro horario o rechaza.
            </Notice>
          )}
          {r.withinAvailability === false && <Notice>Este horario está fuera de tu disponibilidad declarada.</Notice>}

          {open && !pending && price && (
            <>
              <Card className="p-4 text-center bg-[var(--surface-2)]">
                <p className="text-sm text-[var(--text-muted)]">{r.offeredFare ? 'Precio propuesto por el cliente' : 'Tarifa sugerida'}</p>
                <Money value={price} className="block text-3xl font-extrabold" />
                {r.offeredFare && r.suggestedFare && <p className="text-xs text-[var(--text-muted)]">Tarifa sugerida: <Money value={r.suggestedFare.total} /></p>}
              </Card>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="danger" size="lg" onClick={reject}>Rechazar</Button>
                <Button variant="accent" size="lg" loading={busy} onClick={() => void sendQuote({ amount: price, includedServices: [] })}>Aceptar</Button>
              </div>
              <button className="tap text-sm font-medium text-brand-red self-center" onClick={() => setCounter((x) => !x)}>
                {counter ? 'Ocultar' : r.offeredFare ? 'Enviar otro precio (contraoferta)' : 'Ajustar el precio antes de enviarlo'}
              </button>
            </>
          )}
          {open && (counter || pending || !price) && <QuoteForm r={r} busy={busy} onSubmit={sendQuote} />}

          {pending && (
            <Card className="p-4">
              <p className="text-sm text-[var(--text-muted)]">Cotización enviada · vence en {relativeMinutes(pending.expiresAt)}</p>
              <Money value={pending.amount} className="text-2xl font-bold" />
              <Button size="sm" variant="ghost" className="mt-2" onClick={withdraw}>Retirar cotización</Button>
            </Card>
          )}

          <div className="relative">
            <MapView className="h-56 rounded-3xl" markers={markers} route={r.route?.polyline ?? null} interactive={false} />
            {r.route && (
              <span className="absolute left-1/2 top-3 -translate-x-1/2 rounded-full bg-blue-600 text-white px-3 py-1 text-sm font-semibold shadow">{fmtDuration(r.route.durationSeconds)}</span>
            )}
          </div>
          <a className="tap inline-flex items-center gap-1 text-brand-red font-medium text-sm" target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps/dir/?api=1&origin=${r.origin.lat},${r.origin.lng}&destination=${r.destination.lat},${r.destination.lng}&travelmode=driving`}>
            Ver ruta en Google Maps <ExternalLink className="size-4" aria-hidden />
          </a>

          {open && (
            <Button
              variant="secondary"
              icon={<HelpCircle className="size-5" />}
              onClick={() =>
                alert({
                  header: 'Solicitar información',
                  inputs: [{ name: 'message', type: 'textarea', placeholder: '¿Qué necesitas saber?' }],
                  buttons: [{ text: 'Cancelar', role: 'cancel' }, { text: 'Enviar', handler: (v: { message: string }) => void action('request-info', { message: v.message }, 'Pregunta enviada') }],
                })
              }
            >
              Pedir info al cliente
            </Button>
          )}
          {r.rides.map((ride) => (
            <Link key={ride.id} to={`/driver/rides/${ride.id}`}>
              <Button block variant="primary">Ver reserva {ride.code}</Button>
            </Link>
          ))}
          {r.conversationId && (
            <Link to={`/driver/chat/${r.conversationId}`}><Button variant="secondary" block icon={<MessageCircle className="size-5" />}>Chat con el cliente</Button></Link>
          )}
        </div>
      )}
    </Page>
  );
}
